import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import {
  KeyRound, UserPlus, Send, AlertTriangle, Copy, Loader2, RefreshCw, Award,
  ShieldCheck, CheckCircle2, XCircle, RotateCcw, Briefcase, Link2, Sparkles, PenLine,
} from 'lucide-react';
import { toast } from 'sonner';

const PRETTY = {
  children: 'Students', consent_records: 'Consent records', mentor_profiles: 'Mentor profiles',
  mentor_approval_records: 'Mentor approvals', mentor_declaration_records: 'Safeguarding declarations',
  mentor_asks: 'Mentor requests', mentor_recognitions: 'Mentor recognitions', sessions: 'Sessions',
  bookings: 'Experiences', internship_programs: 'Internship programmes',
  internship_opportunities: 'Internship offers', internship_invitations: 'Partner invitations',
  internship_applications: 'Internship applications', internship_proposals: 'Internship proposals',
  internship_attendance: 'Attendance days', school_registrations: 'Registration history',
};

const TYPE_STYLE = {
  DEMO: 'bg-slate-100 text-slate-600', PILOT: 'bg-amber-100 text-amber-700',
  LIVE: 'bg-green-100 text-green-700',
};

export function SchoolManageDialog({ school, onClose, onRefresh, onChangeType, onViewUsers }) {
  const sid = school.school_id;
  const [overview, setOverview] = useState(null);
  const [accounts, setAccounts] = useState(null);
  const [issued, setIssued] = useState({});
  const [sharedPassword, setSharedPassword] = useState('');
  const [busy, setBusy] = useState('');
  const [coordForm, setCoordForm] = useState({ full_name: '', email: '', mobile: '', designation: '' });
  const [health, setHealth] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [resetResult, setResetResult] = useState(null);

  const [aiUsage, setAiUsage] = useState(null);
  const [reflectionUsage, setReflectionUsage] = useState(null);

  const load = useCallback(async () => {
    const [o, a] = await Promise.all([
      api.get(`/admin/schools/${sid}/overview`),
      api.get(`/admin/schools/${sid}/demo-accounts`),
    ]);
    setOverview(o.data);
    setAccounts(a.data);
    api.get(`/admin/schools/${sid}/features/career-buddy-ai/usage`)
      .then((u) => setAiUsage(u.data))
      .catch(() => setAiUsage(null));
    api.get(`/admin/schools/${sid}/features/career-reflection/usage`)
      .then((u) => setReflectionUsage(u.data))
      .catch(() => setReflectionUsage(null));
  }, [sid]);

  useEffect(() => { load().catch(() => toast.error('Could not load this school')); }, [load]);

  const toggleBuddyAI = async (enabled) => {
    setBusy('buddy-ai');
    try {
      await api.put(`/admin/schools/${sid}/features/career-buddy-ai`, { enabled });
      await load();
      toast.success(enabled ? 'My Career Buddy AI switched on' : 'My Career Buddy AI switched off');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not change that setting');
    }
    setBusy('');
  };

  const toggleReflection = async (enabled) => {
    setBusy('career-reflection');
    try {
      await api.put(`/admin/schools/${sid}/features/career-reflection`, { enabled });
      await load();
      toast.success(enabled ? 'Career Reflection & Discovery switched on'
                            : 'Career Reflection & Discovery switched off');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not change that setting');
    }
    setBusy('');
  };

  const applySharedPassword = async () => {
    setBusy('shared-password');
    try {
      const res = await api.post(`/admin/schools/${sid}/demo-accounts/set-password`,
        { password: sharedPassword.trim() });
      setIssued({});
      setSharedPassword('');
      toast.success(`${res.data.updated} demo account${res.data.updated === 1 ? '' : 's'} now use this password`);
      if (res.data.skipped_disabled) {
        toast.info(`${res.data.skipped_disabled} disabled account(s) skipped — restore access first`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not set that password');
    }
    setBusy('');
  };

  const issuePassword = async (userId) => {
    setBusy(userId);
    try {
      const res = await api.post(`/admin/schools/${sid}/demo-accounts/${userId}/reset-password`,
        { confirm: true });
      setIssued(prev => ({ ...prev, [userId]: res.data.password }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not issue a password');
    }
    setBusy('');
  };

  const addCoordinator = async (e) => {
    e.preventDefault();
    setBusy('coordinator');
    try {
      const created = await api.post('/coordinators', {
        ...coordForm, school_id: sid, coordinator_type: 'primary',
      });
      await api.post('/admin/send-activation', { user_id: created.data.id });
      toast.success(`Activation invitation sent to ${created.data.email}`);
      setCoordForm({ full_name: '', email: '', mobile: '', designation: '' });
      await load();
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not add this coordinator');
    }
    setBusy('');
  };

  const resendActivation = async (userId, email) => {
    setBusy(userId);
    try {
      await api.post('/admin/send-activation', { user_id: userId });
      toast.success(`Activation invitation re-sent to ${email}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send the invitation');
    }
    setBusy('');
  };

  const runHealthCheck = async () => {
    setBusy('health');
    setHealth(null);
    try {
      const res = await api.get(`/admin/schools/${sid}/health-check`);
      setHealth(res.data);
      if (res.data.healthy) toast.success(`All ${res.data.checks_run} checks passed`);
      else toast.error(`${res.data.checks_failed} of ${res.data.checks_run} checks need attention`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not run the health check');
    }
    setBusy('');
  };

  const runReset = async () => {
    setBusy('reset');
    try {
      const res = await api.post(`/admin/schools/${sid}/reset-to-master`, { confirm: true });
      setResetResult(res.data);
      setResetting(false);
      toast.success(`${school.school_name} rebuilt from ${res.data.reset_from}`);
      await load();
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not reset this school');
      setResetting(false);
    }
    setBusy('');
  };

  const changeStatus = async (status) => {
    setBusy('status');
    try {
      await api.put(`/admin/schools/${sid}/status`, {
        status, confirm: true,
        note: status === 'CLOSED' ? 'Closed from Platform Admin' : 'Reopened from Platform Admin',
      });
      toast.success(status === 'CLOSED' ? 'Pilot closed — access and email are off' : 'Pilot reopened');
      await load();
      onRefresh?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : detail?.message || 'Could not change status');
    }
    setBusy('');
  };

  const regenerateCertificates = async () => {
    setBusy('certs');
    try {
      const res = await api.post(`/admin/schools/${sid}/regenerate-certificates`, { confirm: true });
      toast.success(`Rebuilt ${res.data.sessions} experience, ${res.data.internships} internship `
        + `and ${res.data.recognitions} recognition certificates`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not rebuild certificates');
    }
    setBusy('');
  };

  const repairCompanyLinks = async () => {
    setBusy('links');
    try {
      const res = await api.post(`/admin/schools/${sid}/repair-internship-company-links`, { confirm: true });
      const d = res.data;
      toast.success(d.opportunities
        ? `Repaired ${d.opportunities} internship(s), ${d.joining_instructions} joining instruction(s), `
          + `${d.attendance} attendance row(s) and ${d.completions} completion note(s)`
        : 'Every internship already points at this school\u2019s own companies');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not repair the company links');
    }
    setBusy('');
  };

  const refreshInternshipDemo = async () => {
    if (!window.confirm(`Replace ${sid}'s internship sample data with a fresh Summer (closed) and `
      + 'Winter (open) programme? The current records are backed up first and no one is emailed.')) return;
    setBusy('internships');
    try {
      const res = await api.post(`/admin/schools/${sid}/refresh-internship-demo`, { confirm: true });
      const c = res.data.created;
      toast.success(`Rebuilt: Summer with ${c.placements} verified placements and Winter with `
        + `${c.winter_applications} applications · ${res.data.certificates.internships} certificates`);
      await load();
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not refresh the internship sample data');
    }
    setBusy('');
  };

  const warnings = overview?.readiness?.warnings || [];
  const buddyAI = {
    enabled: !!overview?.features?.career_buddy_ai?.enabled,
    changed_at: overview?.features?.career_buddy_ai?.changed_at,
    changed_by: overview?.features?.career_buddy_ai?.changed_by,
  };
  const coordinators = (accounts?.accounts || []).filter(a => a.role === 'coordinator');
  const reflection = {
    enabled: !!overview?.features?.career_reflection?.enabled,
    changed_at: overview?.features?.career_reflection?.changed_at,
    changed_by: overview?.features?.career_reflection?.changed_by,
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-2xl sm:max-w-3xl" data-testid="school-manage-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Outfit'] flex items-center gap-3">
            {school.school_logo_url && (
              <img src={school.school_logo_url} alt="" className="h-9 w-9 rounded-lg object-contain bg-white" />
            )}
            {school.school_name}
          </DialogTitle>
          <DialogDescription className="flex flex-wrap items-center gap-2">
            <Badge className="bg-[#FFF0ED] text-[#F97D6A] border-0 font-mono">{sid}</Badge>
            <Badge className="border-0 bg-indigo-50 text-indigo-600"
                   data-testid="manage-school-category">{overview?.school_category || 'DEMO'} school</Badge>
            <Badge className={`border-0 ${TYPE_STYLE[overview?.email_mode] || TYPE_STYLE.DEMO}`}
                   data-testid="manage-school-type">{overview?.email_mode || 'DEMO'} email</Badge>
            <Badge className={`border-0 ${(overview?.status || 'ACTIVE') === 'ACTIVE'
              ? 'bg-green-50 text-green-600' : 'bg-amber-50 text-amber-700'}`}
                   data-testid="manage-school-status">{overview?.status || 'ACTIVE'}</Badge>
            <Badge className="border-0 bg-slate-100 text-slate-600"
                   data-testid="manage-school-board">
              {school.education_board
                ? `${school.education_board}${school.education_board_detail ? ` · ${school.education_board_detail}` : ''}`
                : 'Board not set'}
            </Badge>
            {overview?.email_prefix && (
              <span className="text-xs font-mono text-gray-500">prefix: {overview.email_prefix}</span>
            )}
            {overview?.cloned_from && (
              <span className="text-xs text-gray-400">cloned from {overview.cloned_from}</span>
            )}
          </DialogDescription>
        </DialogHeader>

        {!overview ? (
          <div className="py-12 text-center text-sm text-gray-400">
            <Loader2 className="h-5 w-5 animate-spin mx-auto" />
          </div>
        ) : (
          <Tabs defaultValue="overview">
            <TabsList className="bg-gray-50 rounded-xl p-1">
              <TabsTrigger value="overview" className="rounded-lg text-xs" data-testid="manage-tab-overview">Overview</TabsTrigger>
              <TabsTrigger value="accounts" className="rounded-lg text-xs" data-testid="manage-tab-accounts">Demo accounts</TabsTrigger>
              <TabsTrigger value="coordinators" className="rounded-lg text-xs" data-testid="manage-tab-coordinators">Coordinators</TabsTrigger>
              <TabsTrigger value="lifecycle" className="rounded-lg text-xs" data-testid="manage-tab-lifecycle">Lifecycle</TabsTrigger>
              <TabsTrigger value="features" className="rounded-lg text-xs" data-testid="manage-tab-features">Features</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="pt-4 space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="manage-account-counts">
                {[['Coordinators', overview.accounts.coordinators], ['Parents', overview.accounts.parents],
                  ['Parent mentors', overview.accounts.parent_mentors], ['Partners', overview.accounts.partners]]
                  .map(([label, value]) => (
                    <div key={label} className="rounded-xl bg-gray-50 px-3 py-2.5">
                      <p className="text-lg font-bold text-[#1F2937]">{value}</p>
                      <p className="text-[11px] text-gray-500">{label}</p>
                    </div>
                  ))}
              </div>
              <div className="rounded-xl border border-gray-200 divide-y divide-gray-50 max-h-56 overflow-y-auto"
                   data-testid="manage-record-counts">
                {Object.entries(overview.record_counts || {}).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between px-4 py-2 text-sm">
                    <span className="text-gray-600">{PRETTY[k] || k}</span>
                    <span className="font-semibold text-[#1F2937]">{v}</span>
                  </div>
                ))}
              </div>
              {onViewUsers && (
                <button onClick={() => onViewUsers(sid)}
                        className="text-sm font-semibold text-[#F97D6A] hover:underline"
                        data-testid="view-school-users-link">
                  View this school's user accounts →
                </button>
              )}
              <Button variant="outline" onClick={regenerateCertificates} disabled={busy === 'certs'}
                      className="rounded-xl text-sm w-full h-auto min-h-10 py-2 whitespace-normal text-center" data-testid="regenerate-certificates-btn">
                {busy === 'certs' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                  : <Award className="h-4 w-4 mr-1" />}
                Rebuild certificates with this school's name and logo
              </Button>

              <Button variant="outline" onClick={repairCompanyLinks} disabled={busy === 'links'}
                      className="rounded-xl text-sm w-full h-auto min-h-10 py-2 whitespace-normal text-center" data-testid="repair-company-links-btn">
                {busy === 'links' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                  : <Link2 className="h-4 w-4 mr-1" />}
                Repair internship company links (keeps every record)
              </Button>

              {(overview?.email_mode || 'DEMO') === 'DEMO' && (
                <Button variant="outline" onClick={refreshInternshipDemo} disabled={busy === 'internships'}
                        className="rounded-xl text-sm w-full h-auto min-h-10 py-2 whitespace-normal text-center" data-testid="refresh-internship-demo-btn">
                  {busy === 'internships' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                          : <Briefcase className="h-4 w-4 mr-1" />}
                  Refresh internship sample data (Summer closed · Winter open)
                </Button>
              )}

              <Button variant="outline" onClick={runHealthCheck} disabled={busy === 'health'}
                      className="rounded-xl text-sm w-full h-auto min-h-10 py-2 whitespace-normal text-center" data-testid="run-health-check-btn">
                {busy === 'health' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                   : <ShieldCheck className="h-4 w-4 mr-1" />}
                Run health check on this school's records
              </Button>

              {health && (
                <div className="rounded-xl border border-gray-200 overflow-hidden" data-testid="health-report">
                  <div className={`px-4 py-2.5 flex items-center gap-2 ${health.healthy ? 'bg-green-50' : 'bg-red-50'}`}>
                    {health.healthy ? <CheckCircle2 className="h-4 w-4 text-green-600" />
                                    : <XCircle className="h-4 w-4 text-red-500" />}
                    <p className={`text-sm font-semibold ${health.healthy ? 'text-green-700' : 'text-red-700'}`}
                       data-testid="health-summary">
                      {health.healthy
                        ? `Everything checks out — ${health.checks_run} checks passed`
                        : `${health.checks_failed} of ${health.checks_run} checks need attention`}
                    </p>
                  </div>
                  <div className="max-h-56 overflow-y-auto divide-y divide-gray-50">
                    {health.checks.map(c => (
                      <div key={c.name} className="px-4 py-2 flex items-start gap-2">
                        {c.ok ? <CheckCircle2 className="h-3.5 w-3.5 text-green-500 mt-0.5 shrink-0" />
                              : <XCircle className="h-3.5 w-3.5 text-red-500 mt-0.5 shrink-0" />}
                        <div>
                          <p className="text-xs text-gray-700">{c.name}</p>
                          {!c.ok && c.detail && c.detail !== '[]' && (
                            <p className="text-[11px] font-mono text-red-500 mt-0.5">{c.detail}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {overview.cloned_from && overview.email_mode === 'DEMO' && overview.status === 'ACTIVE' && (
                <div className="rounded-xl bg-gray-50 border border-gray-200 px-4 py-3">
                  <p className="text-sm font-semibold text-[#1F2937]">Refresh before a demo</p>
                  <p className="text-[11px] text-gray-500 mt-0.5 leading-relaxed">
                    Rebuilds this school from {overview.cloned_from} exactly as it stands today,
                    keeping its id, name, logo, email prefix and invite code. Everything added since
                    the last rebuild is discarded, and everyone keeps their current password.
                  </p>
                  <Button variant="outline" onClick={() => setResetting(true)} disabled={busy === 'reset'}
                          className="rounded-xl text-sm mt-2 border-amber-300 text-amber-700 hover:bg-amber-50"
                          data-testid="reset-to-master-btn">
                    <RotateCcw className="h-4 w-4 mr-1" /> Reset to master demo school
                  </Button>
                </div>
              )}
            </TabsContent>

            <TabsContent value="accounts" className="pt-4">
              {(school.school_category || 'DEMO').toUpperCase() === 'DEMO' && (
              <div className="rounded-xl border border-gray-200 px-4 py-3 mb-3 bg-[#F9FAFB]"
                   data-testid="set-shared-password-row">
                <p className="text-xs font-semibold text-[#1F2937]">Set one password for every demo account</p>
                <p className="text-[11px] text-gray-500 mt-0.5 leading-relaxed">
                  Use this when you keep having to reissue passwords: every fictional account in this
                  school gets the password you type, so a sign-out never locks you out again.
                </p>
                <div className="flex gap-2 mt-2.5">
                  <Input
                    value={sharedPassword}
                    onChange={(e) => setSharedPassword(e.target.value)}
                    placeholder="At least 6 characters"
                    className="h-9 rounded-xl text-sm"
                    data-testid="shared-password-input"
                  />
                  <Button onClick={applySharedPassword}
                          disabled={sharedPassword.trim().length < 6 || busy === 'shared-password'}
                          className="h-9 rounded-xl bg-[#F97D6A] text-white hover:bg-[#F06651] text-sm shrink-0"
                          data-testid="apply-shared-password-btn">
                    {busy === 'shared-password'
                      ? <Loader2 className="h-4 w-4 animate-spin" />
                      : <><KeyRound className="h-3.5 w-3.5 mr-1" /> Apply to all</>}
                  </Button>
                </div>
              </div>
              )}
              <div className="rounded-xl border border-gray-200 max-h-80 overflow-y-auto"
                   data-testid="demo-accounts-list">
                <table className="w-full text-xs">
                  <tbody>
                    {(accounts?.accounts || []).concat(accounts?.partners || []).map(a => (
                      <tr key={a.id} className="border-b border-gray-50 last:border-0">
                        <td className="py-2 px-3">
                          <p className="font-semibold text-[#1F2937]">{a.full_name}</p>
                          <p className="text-[10px] uppercase tracking-wide text-gray-400">
                            {a.role}{a.is_mentor ? ' · mentor' : ''}{a.company ? ` · ${a.company}` : ''}
                          </p>
                        </td>
                        <td className="py-2 px-3 font-mono text-[11px] text-gray-600">{a.email}</td>
                        <td className="py-2 px-3 text-right">
                          {issued[a.id] ? (
                            <button onClick={() => { navigator.clipboard.writeText(issued[a.id]); toast.success('Password copied'); }}
                                    className="font-mono text-[11px] text-[#F97D6A] inline-flex items-center gap-1"
                                    data-testid={`issued-password-${a.id}`}>
                              {issued[a.id]} <Copy className="h-3 w-3" />
                            </button>
                          ) : (
                            <Button variant="ghost" size="sm" disabled={busy === a.id}
                                    onClick={() => issuePassword(a.id)}
                                    className="h-7 text-[11px] rounded-lg text-gray-500"
                                    data-testid={`issue-password-${a.id}`}>
                              <KeyRound className="h-3 w-3 mr-1" /> New password
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-[11px] text-gray-400 mt-2">
                Passwords are stored only as hashes, so they cannot be looked up — issuing a new one
                shows it here once.
              </p>
            </TabsContent>

            <TabsContent value="coordinators" className="pt-4 space-y-4">
              <div className="rounded-xl border border-gray-200 divide-y divide-gray-50"
                   data-testid="manage-coordinators-list">
                {coordinators.length === 0 && (
                  <p className="text-sm text-gray-400 px-4 py-6 text-center">No coordinators yet</p>
                )}
                {coordinators.map(c => (
                  <div key={c.id} className="flex items-center justify-between px-4 py-2.5">
                    <div>
                      <p className="text-sm font-semibold text-[#1F2937]">{c.full_name}</p>
                      <p className="text-[11px] font-mono text-gray-500">{c.email}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={`border-0 text-[10px] ${c.status === 'active' ? 'bg-green-50 text-green-600' : c.status === 'pending_activation' ? 'bg-amber-50 text-amber-700' : 'bg-gray-100 text-gray-500'}`}
                             data-testid={`coordinator-status-${c.id}`}>
                        {c.status === 'active' ? 'Active'
                          : c.status === 'pending_activation' ? 'Pending activation'
                            : c.status.replace(/_/g, ' ')}
                      </Badge>
                      {c.can_resend_activation && (
                        <Button variant="ghost" size="sm" disabled={busy === c.id}
                                onClick={() => resendActivation(c.id, c.email)}
                                className="h-7 text-[11px] rounded-lg"
                                data-testid={`resend-activation-${c.id}`}>
                          <Send className="h-3 w-3 mr-1" /> Re-send
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <form onSubmit={addCoordinator} className="rounded-xl bg-gray-50 p-4 space-y-3"
                    data-testid="add-coordinator-form">
                <p className="text-sm font-semibold text-[#1F2937] flex items-center gap-2">
                  <UserPlus className="h-4 w-4 text-[#F97D6A]" /> Add a Career Coordinator
                </p>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[11px] font-semibold text-gray-600">Full name</Label>
                    <Input required value={coordForm.full_name}
                           onChange={(e) => setCoordForm({ ...coordForm, full_name: e.target.value })}
                           className="rounded-xl mt-1 h-9 text-sm" data-testid="coordinator-name-input" />
                  </div>
                  <div>
                    <Label className="text-[11px] font-semibold text-gray-600">Email</Label>
                    <Input required type="email" value={coordForm.email}
                           onChange={(e) => setCoordForm({ ...coordForm, email: e.target.value })}
                           className="rounded-xl mt-1 h-9 text-sm" data-testid="coordinator-email-input" />
                  </div>
                  <div>
                    <Label className="text-[11px] font-semibold text-gray-600">Mobile</Label>
                    <Input required value={coordForm.mobile}
                           onChange={(e) => setCoordForm({ ...coordForm, mobile: e.target.value })}
                           className="rounded-xl mt-1 h-9 text-sm" data-testid="coordinator-mobile-input" />
                  </div>
                  <div>
                    <Label className="text-[11px] font-semibold text-gray-600">Designation</Label>
                    <Input value={coordForm.designation}
                           onChange={(e) => setCoordForm({ ...coordForm, designation: e.target.value })}
                           placeholder="Career Coordinator"
                           className="rounded-xl mt-1 h-9 text-sm" data-testid="coordinator-designation-input" />
                  </div>
                </div>
                <p className="text-[11px] text-gray-500">
                  Creates a coordinator for {sid} only — never a platform admin — and emails them a
                  secure activation link so they choose their own password.
                </p>
                <Button type="submit" disabled={busy === 'coordinator'}
                        className="bg-[#F97D6A] hover:bg-[#F06651] rounded-xl text-sm"
                        data-testid="submit-coordinator-btn">
                  {busy === 'coordinator' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                          : <Send className="h-4 w-4 mr-1" />}
                  Add and send invitation
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="lifecycle" className="pt-4 space-y-4">
              <div className="rounded-xl border border-gray-200 px-4 py-3">
                <p className="text-sm text-gray-600">
                  A <span className="font-semibold text-[#1F2937]">{overview.school_category}</span>{' '}
                  school on <span className="font-semibold text-[#1F2937]">{overview.email_mode}</span>{' '}
                  email routing, currently {(overview.status || 'ACTIVE').toLowerCase()}.
                  {overview.email_mode === 'DEMO' && ' All email goes only to the monitoring inbox.'}
                  {overview.email_mode === 'PILOT' && ' Real recipients receive email, with a monitoring copy.'}
                  {overview.email_mode === 'LIVE' && ' Email is delivered normally, with no monitoring copy.'}
                </p>
              </div>
              {warnings.length > 0 && overview.school_category !== 'DEMO' && (
                <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 space-y-1"
                     data-testid="lifecycle-warnings">
                  <p className="text-sm font-semibold text-amber-800 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" /> Before this school's email reaches real people
                  </p>
                  {warnings.map(w => (
                    <p key={w} className="text-xs text-amber-800">• {w}</p>
                  ))}
                  <p className="text-[11px] text-amber-700 pt-1">
                    Nothing is deleted or converted automatically — demo history stays exactly as it is.
                  </p>
                </div>
              )}
              <Button variant="outline" onClick={() => onChangeType?.(school)}
                      className="rounded-xl text-sm w-full" data-testid="open-change-type-btn">
                <RefreshCw className="h-4 w-4 mr-1" /> Change email routing
              </Button>
              {overview.school_category === 'PILOT' && (
                <Button variant="outline" disabled={busy === 'status'}
                        onClick={() => changeStatus(overview.status === 'ACTIVE' ? 'CLOSED' : 'ACTIVE')}
                        className={`rounded-xl text-sm w-full ${overview.status === 'ACTIVE'
                          ? 'border-amber-300 text-amber-700 hover:bg-amber-50' : ''}`}
                        data-testid="toggle-pilot-status-btn">
                  {busy === 'status' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                  {overview.status === 'ACTIVE' ? 'Close this pilot' : 'Reopen this pilot'}
                </Button>
              )}
              {overview.status !== 'ACTIVE' && (
                <p className="text-[11px] text-gray-500">
                  Everyone at this school is signed out and no email leaves it, but every record,
                  piece of feedback and certificate is kept for audit.
                </p>
              )}
              {overview.school_category !== 'LIVE' && (
                <p className="text-[11px] text-gray-400">
                  A pilot never becomes a live school here — a live school is created only through the
                  official registration and agreement workflow, which allocates its own SC number.
                </p>
              )}
            </TabsContent>

            <TabsContent value="features" className="pt-4 space-y-4" data-testid="manage-features-tab">
              <p className="text-xs text-gray-500">
                Features this school can use. Switching one on or off takes effect immediately for
                everyone at {school.school_name}.
              </p>
              <div className="rounded-xl border border-gray-200 px-4 py-3.5"
                   data-testid="buddy-ai-feature-row">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-[#1F2937] flex items-center gap-1.5">
                      <Sparkles className="h-3.5 w-3.5 text-[#F97D6A]" /> My Career Buddy AI
                    </p>
                    <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                      Lets this school's parents open the AI career companion on their child's page and
                      ask about careers, subjects and study paths. Switching it off closes access
                      without deleting any saved conversations.
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1.5 shrink-0">
                    <Switch
                      checked={buddyAI.enabled}
                      disabled={busy === 'buddy-ai'}
                      onCheckedChange={toggleBuddyAI}
                      data-testid="buddy-ai-feature-switch"
                    />
                    <span className={`text-[11px] font-semibold ${buddyAI.enabled ? 'text-green-600' : 'text-gray-400'}`}
                          data-testid="buddy-ai-feature-state">
                      {buddyAI.enabled ? 'On' : 'Off'}
                    </span>
                  </div>
                </div>
                {buddyAI.changed_at && (
                  <p className="text-[11px] text-gray-400 mt-3 pt-3 border-t border-gray-100"
                     data-testid="buddy-ai-feature-history">
                    Last changed by {buddyAI.changed_by || 'Platform Admin'} on{' '}
                    {new Date(buddyAI.changed_at).toLocaleDateString('en-GB',
                      { day: 'numeric', month: 'short', year: 'numeric' })}
                  </p>
                )}
              </div>

              {aiUsage && (
                <div className="rounded-xl border border-gray-200 px-4 py-3.5"
                     data-testid="buddy-ai-usage-summary">
                  <div className="flex items-baseline justify-between gap-3">
                    <p className="text-sm font-semibold text-[#1F2937]">Usage summary</p>
                    <span className="text-[11px] text-gray-400" data-testid="buddy-ai-usage-month">
                      {new Date(`${aiUsage.month}-01T00:00:00Z`).toLocaleDateString('en-GB',
                        { month: 'long', year: 'numeric', timeZone: 'UTC' })}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    {[
                      { k: 'children', label: 'Children using AI', testid: 'buddy-ai-usage-children',
                        value: aiUsage.children_using.toLocaleString() },
                      { k: 'questions', label: 'Questions this month', testid: 'buddy-ai-usage-questions',
                        value: aiUsage.questions_this_month.toLocaleString() },
                      { k: 'tokens', label: 'Tokens this month', testid: 'buddy-ai-usage-tokens',
                        value: aiUsage.tokens_this_month.recorded
                          ? aiUsage.tokens_this_month.total.toLocaleString() : 'Not recorded' },
                      { k: 'cost', label: 'Estimated model cost', testid: 'buddy-ai-usage-cost',
                        value: aiUsage.estimated_cost_usd === null
                          ? 'Unavailable' : `$${aiUsage.estimated_cost_usd.toFixed(4)}` },
                    ].map((m) => (
                      <div key={m.k} className="rounded-lg bg-gray-50 px-3 py-2.5" data-testid={m.testid}>
                        <p className="text-base font-semibold text-[#1F2937] tabular-nums">{m.value}</p>
                        <p className="text-[11px] text-gray-500 mt-0.5">{m.label}</p>
                      </div>
                    ))}
                  </div>
                  <p className="text-[11px] text-gray-400 mt-3 leading-relaxed"
                     data-testid="buddy-ai-usage-note">
                    Children using AI counts every child at this school who has ever asked a question.
                    The other three cover {new Date(`${aiUsage.month}-01T00:00:00Z`).toLocaleDateString(
                      'en-GB', { month: 'long', timeZone: 'UTC' })} only. Cost is an estimate from the
                    tokens actually reported by {aiUsage.model}
                    {aiUsage.pricing_source ? ` at ${aiUsage.pricing_source}` : ''} — not the Emergent
                    credits charged.
                    {aiUsage.tokens_this_month.questions_without_tokens > 0 && (
                      <> Tokens are unavailable for {aiUsage.tokens_this_month.questions_without_tokens}{' '}
                      question{aiUsage.tokens_this_month.questions_without_tokens === 1 ? '' : 's'} asked
                      before token recording began, so the figures above cover{' '}
                      {aiUsage.tokens_this_month.questions_counted} of{' '}
                      {aiUsage.questions_this_month}.</>
                    )}
                  </p>
                </div>
              )}

              <div className="rounded-xl border border-gray-200 px-4 py-3.5"
                   data-testid="career-reflection-feature-row">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-[#1F2937] flex items-center gap-1.5">
                      <PenLine className="h-3.5 w-3.5 text-[#F97D6A]" /> Career Reflection &amp; Discovery
                    </p>
                    <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                      Lets parents record their child's own thoughts after a completed session, visit
                      or internship, and shows a short AI summary of what the child is discovering.
                      Works whether or not My Career Buddy AI is on. Switching it off hides both from
                      parents and keeps every reflection saved.
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1.5 shrink-0">
                    <Switch
                      checked={reflection.enabled}
                      disabled={busy === 'career-reflection'}
                      onCheckedChange={toggleReflection}
                      data-testid="career-reflection-feature-switch"
                    />
                    <span className={`text-[11px] font-semibold ${reflection.enabled ? 'text-green-600' : 'text-gray-400'}`}
                          data-testid="career-reflection-feature-state">
                      {reflection.enabled ? 'On' : 'Off'}
                    </span>
                  </div>
                </div>
                {reflection.changed_at && (
                  <p className="text-[11px] text-gray-400 mt-3 pt-3 border-t border-gray-100"
                     data-testid="career-reflection-feature-history">
                    Last changed by {reflection.changed_by || 'Platform Admin'} on{' '}
                    {new Date(reflection.changed_at).toLocaleDateString('en-GB',
                      { day: 'numeric', month: 'short', year: 'numeric' })}
                  </p>
                )}
              </div>

              {reflectionUsage && (
                <div className="rounded-xl border border-gray-200 px-4 py-3.5"
                     data-testid="career-reflection-usage-summary">
                  <div className="flex items-baseline justify-between gap-3">
                    <p className="text-sm font-semibold text-[#1F2937]">Reflection usage</p>
                    <span className="text-[11px] text-gray-400">
                      {new Date(`${reflectionUsage.month}-01T00:00:00Z`).toLocaleDateString('en-GB',
                        { month: 'long', year: 'numeric', timeZone: 'UTC' })}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    {[
                      { k: 'children', label: 'Children with reflections', testid: 'reflection-usage-children',
                        value: reflectionUsage.children_reflecting.toLocaleString() },
                      { k: 'total', label: 'Reflections recorded', testid: 'reflection-usage-total',
                        value: reflectionUsage.reflections_total.toLocaleString() },
                      { k: 'updates', label: 'Discovery updates this month', testid: 'reflection-usage-updates',
                        value: reflectionUsage.discovery_updates_this_month.toLocaleString() },
                      { k: 'cost', label: 'Estimated model cost', testid: 'reflection-usage-cost',
                        value: reflectionUsage.estimated_cost_usd === null
                          ? 'Unavailable' : `$${reflectionUsage.estimated_cost_usd.toFixed(4)}` },
                    ].map((m) => (
                      <div key={m.k} className="rounded-lg bg-gray-50 px-3 py-2.5" data-testid={m.testid}>
                        <p className="text-base font-semibold text-[#1F2937] tabular-nums">{m.value}</p>
                        <p className="text-[11px] text-gray-500 mt-0.5">{m.label}</p>
                      </div>
                    ))}
                  </div>
                  <p className="text-[11px] text-gray-400 mt-3 leading-relaxed"
                     data-testid="reflection-usage-note">
                    Discovery summaries are written only when a reflection is added or edited, and are
                    counted separately from My Career Buddy AI chat.
                    {reflectionUsage.tokens_this_month.recorded
                      ? ` ${reflectionUsage.tokens_this_month.total.toLocaleString()} tokens this month`
                      : ' No summaries written this month'} — estimated from {reflectionUsage.model}
                    {reflectionUsage.pricing_source ? ` at ${reflectionUsage.pricing_source}` : ''}, not
                    the Emergent credits charged.
                  </p>
                </div>
              )}

            </TabsContent>

          </Tabs>
        )}

        {resetting && (
          <Dialog open onOpenChange={() => setResetting(false)}>
            <DialogContent className="rounded-2xl sm:max-w-md" data-testid="reset-confirm-dialog">
              <DialogHeader>
                <DialogTitle className="font-['Outfit'] flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-500" /> Rebuild {school.school_name}?
                </DialogTitle>
                <DialogDescription>
                  Its {overview?.total_records || ''} records are deleted and copied fresh from{' '}
                  {overview?.cloned_from}. Anything added during earlier demos is lost. The{' '}
                  {overview?.accounts?.total || ''} accounts are rebuilt too, but{' '}
                  <strong>everyone keeps the password they sign in with today</strong>. The school
                  keeps its id, name, logo, prefix and invite code, stays in DEMO mode, and no email
                  is sent.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={() => setResetting(false)} className="rounded-xl">
                  Cancel
                </Button>
                <Button onClick={runReset} disabled={busy === 'reset'}
                        className="bg-[#F97D6A] hover:bg-[#F06651] rounded-xl"
                        data-testid="confirm-reset-btn">
                  {busy === 'reset' ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Rebuilding…</>
                                    : 'Yes, rebuild it'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {resetResult && (
          <Dialog open onOpenChange={() => setResetResult(null)}>
            <DialogContent className="rounded-2xl sm:max-w-2xl" data-testid="reset-result-dialog">
              <DialogHeader>
                <DialogTitle className="font-['Outfit'] flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-600" /> {resetResult.school_name} is fresh again
                </DialogTitle>
                <DialogDescription>
                  {resetResult.total_records} records rebuilt from {resetResult.reset_from} ·{' '}
                  invite code {resetResult.invite_code} ·{' '}
                  {(resetResult.certificates?.sessions || 0) + (resetResult.certificates?.internships || 0)
                   + (resetResult.certificates?.recognitions || 0)} certificates regenerated.{' '}
                  {resetResult.passwords_kept
                    ? `All ${resetResult.passwords_kept} accounts kept their existing passwords, so everyone can still sign in.`
                    : 'These passwords are shown once.'}
                </DialogDescription>
              </DialogHeader>
              <div className="max-h-72 overflow-y-auto rounded-xl border border-gray-200">
                <table className="w-full text-xs">
                  <tbody>
                    {(resetResult.accounts || []).map(a => (
                      <tr key={a.id} className="border-b border-gray-50 last:border-0">
                        <td className="py-2 px-3 font-semibold text-[#1F2937]">{a.full_name}</td>
                        <td className="py-2 px-3 font-mono text-[11px] text-gray-600">{a.email}</td>
                        <td className="py-2 px-3 font-mono text-[11px] text-[#F97D6A]">
                          {a.password_kept ? <span className="text-gray-400 font-sans">unchanged</span> : a.password}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <DialogFooter>
                <Button variant="outline" className="rounded-xl"
                        onClick={() => {
                          navigator.clipboard.writeText((resetResult.accounts || [])
                            .map(a => `${a.role}\t${a.email}\t${a.password_kept ? 'unchanged' : a.password}`).join('\n'));
                          toast.success('Demo logins copied');
                        }} data-testid="copy-reset-credentials">
                  <Copy className="h-4 w-4 mr-1" /> Copy all
                </Button>
                <Button onClick={() => setResetResult(null)}
                        className="bg-[#F97D6A] hover:bg-[#F06651] rounded-xl"
                        data-testid="close-reset-result">Done</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default SchoolManageDialog;
